import { ApiError } from './client';

/**
 * What to tell somebody when a request failed.
 *
 * @param error The rejection, which may or may not be an {@link ApiError}.
 * @param fallback
 * What this particular action says when nothing more specific is known - "ثبت مهلت انجام
 * نشد." rather than a generic sentence. The caller knows what the person was trying to do and
 * this module does not, so the fallback belongs at the call site.
 *
 * @remarks
 * <b>The server's `detail` is never rendered.</b> It is English, its dates are Gregorian, and
 * it is not free of the vocabulary CLAUDE.md non-negotiable 7 keeps off a non-technical
 * user's screen - `GitLabErrors` alone produces "GitLab could not be reached", "GitLab
 * rejected the credential" and "was not found in GitLab", and `ResultProblemDetails` copies
 * `Error.Message` into `detail` verbatim.
 *
 * `ErrorState` and one dialog already followed this rule and said so in their comments;
 * sixteen other places rendered `failure.message` straight into the page. The rule was
 * written down, and then not shared - so this exists to be the only way to turn a failure
 * into a sentence.
 *
 * Administrator screens are the deliberate exception and do not use this: an operator needs
 * the code and the server's own words, and `SyncHealthPage` explains why in its own remarks.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) {
    // A network failure, or something that is not a response at all. The fallback describes
    // the action, which is more use than "TypeError: Failed to fetch".
    return fallback;
  }

  return MESSAGES[error.errorCode] ?? fallback;
}

/**
 * A Persian sentence per structured error code.
 *
 * @remarks
 * Keyed on `errorCode` rather than on status, because the status says how the request failed
 * and the code says what happened. Codes absent from here fall back to the caller's sentence;
 * that is deliberate, and is why a new code on the server cannot leak its English through
 * this path by default.
 */
const MESSAGES: Record<string, string> = {
  // Read-only mode is a designed state, not a failure - so it is described as one.
  'GitLab.Unavailable':
    'سامانه مرجع در دسترس نیست. اطلاعات نمایش‌داده‌شده ممکن است به‌روز نباشد و ثبت تغییر موقتاً ممکن نیست.',

  'GitLab.Unauthorized': 'اتصال به سامانه مرجع برقرار نیست. با مدیر سامانه تماس بگیرید.',
  'GitLab.Forbidden': 'اجازهٔ این کار در سامانه مرجع به شما داده نشده است.',
  'GitLab.NotFound': 'این مورد در سامانه مرجع پیدا نشد. ممکن است حذف شده باشد.',
  'GitLab.RateLimited': 'سامانه مرجع موقتاً درخواست‌ها را محدود کرده است. کمی بعد دوباره تلاش کنید.',
  'GitLab.Rejected': 'سامانه مرجع این تغییر را نپذیرفت. مقادیر واردشده را بررسی کنید.',
  'GitLab.AlreadyExists': 'موردی با همین مشخصات از قبل وجود دارد.',
  'GitLab.UnexpectedResponse': 'پاسخ سامانه مرجع قابل پردازش نبود. اگر ادامه داشت به مدیر سامانه اطلاع دهید.',

  'Auth.GitLabCredentialUnavailable': 'نشست شما پایان یافته است. لطفاً دوباره وارد شوید.',
  'Auth.SessionRejected': 'نشست شما پایان یافته است. لطفاً دوباره وارد شوید.',
  'Auth.NotAuthenticated': 'برای این کار باید وارد شوید.',

  'Task.Orphaned': 'این وظیفه دیگر در سامانه وجود ندارد و قابل تغییر نیست.',
  'Project.NotFound': 'مورد درخواستی وجود ندارد یا به آن دسترسی ندارید.',
  'Task.NotFound': 'مورد درخواستی وجود ندارد یا به آن دسترسی ندارید.',

  'Capacity.Forbidden': 'تنها یک برنامه‌ریز می‌تواند ظرفیت یا مرخصی دیگران را تغییر دهد.',
  'Leave.Forbidden': 'اجازهٔ دیدن مرخصی این فرد را ندارید.',
  'Workload.Forbidden': 'دیدن بار کاری تیم تنها برای برنامه‌ریزان ممکن است.',
  'People.Forbidden': 'دیدن فهرست افراد تنها برای مدیر سامانه ممکن است.',

  'Collaborator.NotAProjectMember':
    'فقط اعضای همین پروژه را می‌توان به‌عنوان کمک اجرایی افزود. فهرست را بررسی کنید.',

  'SmsDirect.NoMobileNumber':
    'برای این همکار شمارهٔ موبایل ثبت نشده است. ابتدا آن را در پروفایل او ثبت کنید.',
  'SmsRouteTest.NoMobileNumber':
    'شمارهٔ موبایل شما در پروفایل ثبت نشده است. ابتدا آن را از «پروفایل من» ثبت کنید.',
  'Notification.NotConfigured':
    'اتصال به سرویس اطلاع‌رسانی تنظیم نشده است (کلید اتصال ثبت نشده).',
  'Notification.Unavailable': 'سرویس اطلاع‌رسانی در دسترس نیست. کمی بعد دوباره تلاش کنید.',
  'Notification.Unauthorized': 'کلید اتصال به سرویس اطلاع‌رسانی پذیرفته نشد.',

  'Validation.Failed': 'اطلاعات واردشده کامل یا معتبر نیست.',
};

/** The codes this module has a sentence for, so a test can assert the set rather than guess. */
export const TRANSLATED_ERROR_CODES = Object.keys(MESSAGES);
